import { useCallback, useEffect, useState } from "react";
import { ApiClient, ApiResult, DataApiClient } from "../api/dataApiClient";
import { AdminApiClient } from "../api/adminApiClient";
import { useDataApiClient, useAdminApiClient } from "./useApiClient";
import { useDidUnmount } from "./useDidUnmount";

export type ApiRequestState<T> = {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  statusCode: number | null;
  next?: string;
  result: ApiResult<T> | null;
};

export const useDataApiRequest = <T>(
  fn: (apiClient: DataApiClient) => Promise<ApiResult<T>> | null,
  deps: any[]
) => {
  return useApiRequest(fn, useDataApiClient(), deps);
};

export const useAdminApiRequest = <T>(
  fn: (apiClient: AdminApiClient) => Promise<ApiResult<T>> | null,
  deps: any[]
) => {
  return useApiRequest(fn, useAdminApiClient(), deps);
};

const useApiRequest = <T, T2>(
  fn: (apiClient: T2) => Promise<ApiResult<T>> | null,
  apiClient: T2,
  deps: any[]
) => {
  const didUnmount = useDidUnmount();
  const [refreshCount, setRefrsehCount] = useState<number>(0);

  const refresh = useCallback(
    () => setRefrsehCount((state) => state + 1),
    [setRefrsehCount]
  );

  const [apiState, setApiState] = useState<ApiRequestState<T>>({
    data: null,
    isLoading: true,
    error: null,
    statusCode: null,
    result: null,
  });

  useEffect(() => {
    setApiState((state) => ({ ...state, isLoading: true }));
    const requestPromise = fn(apiClient);

    if (!requestPromise) {
      setApiState((state) => ({ ...state, data: null, isLoading: false }));
      return;
    }

    requestPromise
      .then((result) => {
        if (didUnmount.current) {
          return;
        }
        if (result.ok) {
          setApiState({
            isLoading: false,
            data: result.data,
            error: null,
            statusCode: result.statusCode,
            next: result.next,
            result,
          });
        } else {
          setApiState({
            isLoading: false,
            data: null,
            error: `HTTP ${result.statusCode} error`,
            statusCode: result.statusCode ?? null,
            result,
          });
        }
      })
      .catch((error) => {
        if (didUnmount.current) {
          return;
        }
        setApiState({
          isLoading: false,
          data: null,
          error: "Unknown error.",
          statusCode: null,
          result: null,
        });
      });
  }, [didUnmount, apiClient, setApiState, refreshCount, ...deps]);

  return { ...apiState, refresh };
};
